import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { EmployeeMultiSelectOption } from '../../../components/employees/EmployeeMultiSelect';
import { UI_Z_CLASS } from '../../../utils/uiZIndex';
import { SETTINGS_BTN_OUTLINE } from '../../settings/utils/settingsSectionStyles';
import type { FuelProvider, VehicleListItem, VehicleStatus } from '../types';
import { FUEL_PROVIDER_OPTIONS, VEHICLE_STATUS_OPTIONS } from '../types';
import { DateField, DriverField, SelectField, TextField } from './FuhrparkFormFields';

export type VehicleFormValues = {
  brand: string;
  model: string;
  plate: string;
  year: string;
  first_registration: string;
  hu_due_on: string;
  mileage: string;
  vin: string;
  assigned_employee_id: string;
  status: VehicleStatus;
  fuel_provider: FuelProvider | 'none';
  fuel_limit: string;
};

export function emptyVehicleForm(): VehicleFormValues {
  return {
    brand: '',
    model: '',
    plate: '',
    year: '',
    first_registration: '',
    hu_due_on: '',
    mileage: '',
    vin: '',
    assigned_employee_id: '',
    status: 'inactive',
    fuel_provider: 'none',
    fuel_limit: '',
  };
}

export function vehicleToForm(v: VehicleListItem & { hu_due_on?: string | null }): VehicleFormValues {
  return {
    brand: v.brand,
    model: v.model,
    plate: v.plate,
    year: v.year != null ? String(v.year) : '',
    first_registration: v.first_registration ?? '',
    hu_due_on: v.hu_due_on ?? '',
    mileage: v.mileage ? String(v.mileage) : '',
    vin: v.vin,
    assigned_employee_id: v.assigned_employee_id ?? '',
    status: v.status,
    fuel_provider: v.fuel?.provider ?? 'none',
    fuel_limit: v.fuel ? String(v.fuel.monthly_limit_cents / 100) : '',
  };
}

export function VehicleFormModal({
  open,
  title,
  values,
  employees,
  saving,
  error,
  showFuelOnCreate,
  onChange,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  values: VehicleFormValues;
  employees: EmployeeMultiSelectOption[];
  saving: boolean;
  error: string | null;
  showFuelOnCreate?: boolean;
  onChange: (next: VehicleFormValues) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const set = <K extends keyof VehicleFormValues>(key: K, value: VehicleFormValues[K]) =>
    onChange({ ...values, [key]: value });

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[data-app-select-list]')) return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className={`fixed inset-0 ${UI_Z_CLASS.plantafelDrawer} flex justify-end`}>
      <button type="button" className="absolute inset-0 bg-black/25" aria-label="Schließen" onClick={onClose} />
      <div
        className="relative flex h-full w-full max-w-[480px] flex-col border-l border-app-border bg-app-surface shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fuhrpark-vehicle-form-title"
      >
        <div className="flex items-start justify-between border-b border-app-border px-5 py-4">
          <h2 id="fuhrpark-vehicle-form-title" className="text-base font-semibold text-app-text">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-md text-app-text-subtle hover:bg-app-hover"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField label="Marke" value={values.brand} onChange={(v) => set('brand', v)} />
            <TextField label="Modell" value={values.model} onChange={(v) => set('model', v)} required />
            <TextField label="Kennzeichen" value={values.plate} onChange={(v) => set('plate', v)} required kind="plate" />
            <TextField label="Baujahr" value={values.year} onChange={(v) => set('year', v)} kind="year" />
            <DateField
              label="Erstzulassung"
              value={values.first_registration}
              onChange={(v) => set('first_registration', v)}
            />
            <DateField
              label="Nächste HU/AU"
              value={values.hu_due_on}
              onChange={(v) => set('hu_due_on', v)}
            />
            <TextField label="Kilometerstand" value={values.mileage} onChange={(v) => set('mileage', v)} kind="int" />
            <TextField label="FIN" value={values.vin} onChange={(v) => set('vin', v)} kind="vin" />
            <SelectField
              label="Status"
              value={values.status}
              onChange={(v) => set('status', v as VehicleStatus)}
              options={VEHICLE_STATUS_OPTIONS}
            />
            <DriverField
              employees={employees}
              selectedId={values.assigned_employee_id}
              onChange={(id) => set('assigned_employee_id', id)}
            />
          </div>
          {showFuelOnCreate ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectField
                label="Tankkarten-Anbieter"
                value={values.fuel_provider}
                onChange={(v) => set('fuel_provider', v as FuelProvider | 'none')}
                options={FUEL_PROVIDER_OPTIONS}
              />
              <TextField
                label="Tanklimit (€ / Monat)"
                value={values.fuel_limit}
                onChange={(v) => set('fuel_limit', v)}
                kind="money"
              />
            </div>
          ) : null}
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>
        <div className="flex gap-2 border-t border-app-border px-5 py-3">
          <button type="button" className={`${SETTINGS_BTN_OUTLINE} flex-1`} onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button
            type="button"
            className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838] disabled:opacity-40"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? <LoadingSpinner size="sm" /> : null}
            Speichern
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
